import { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import axios from "axios";
import { FadeUp, CountUp } from "./Reveal";
import { EmSwash } from "./EmSwash";

/**
 * ImpactBand — "Where the money went." Three live figures pulled from the
 * grants ledger: dollars disbursed, students funded, grants sent.
 *
 * Renders nothing until real money has moved (`ready`) AND the founder has
 * flipped `IMPACT_BAND_ENABLED=true` — unless `forceShow` (mockups) or a
 * `sample` payload is supplied.
 *
 * NOT yet mounted on the landing page — see /demo/impact-band.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const dollars = (cents) => Math.round(cents / 100);
const sinceLabel = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: "long", year: "numeric" }) : null);

export const ImpactBand = ({ sample = null, forceShow = false, testid = "impact-band" }) => {
  const [data, setData] = useState(sample);

  useEffect(() => {
    if (sample) { setData(sample); return; }
    axios.get(`${API}/impact`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [sample]);

  if (!data) return null;
  if (!forceShow && !(data.ready && data.enabled)) return null;

  const since = sinceLabel(data.first_grant_at);
  const figures = [
    { key: "disbursed", label: "Disbursed to students", value: dollars(data.grants_disbursed_cents), prefix: "$", note: "sent by My Rich Aunt as grants" },
    { key: "students", label: "Students funded", value: data.students_funded, note: data.students_funded === 1 ? "verified, enrolled student" : "verified, enrolled students" },
    { key: "grants", label: "Grants sent", value: data.grants_count, note: since ? `since ${since}` : "and counting" },
  ];

  return (
    <section data-testid={testid} className="relative overflow-hidden bg-ink text-sand">
      <span aria-hidden className="pointer-events-none absolute -left-24 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full bg-tangerine/20 blur-3xl" />
      <span aria-hidden className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-yellow/10 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-6 py-24 lg:px-8 lg:py-32">
        <div className="grid grid-cols-1 gap-14 lg:grid-cols-12 lg:gap-x-12">
          <div className="lg:col-span-5">
            <FadeUp>
              <span className="text-[13px] font-semibold uppercase tracking-[0.22em] text-yellow" data-testid={`${testid}-eyebrow`}>
                Where the money went
              </span>
              <h2 className="mt-4 font-serif text-3xl lg:text-[3.5rem] leading-[1.02] tracking-tight text-sand" data-testid={`${testid}-heading`}>
                Every dollar below reached a <EmSwash>real student.</EmSwash>
              </h2>
              <p className="mt-6 max-w-md text-base leading-relaxed text-sand/75">
                Donations settle to My Rich Aunt. Grants go out from our balance to verified students' bank accounts, and Stripe confirms each one. These figures update as transfers clear.
              </p>
            </FadeUp>
            <FadeUp delay={0.1}>
              <a
                href="/campaigns"
                data-testid={`${testid}-cta`}
                className="group mt-8 inline-flex items-center gap-2 rounded-full border-2 border-sand/30 px-6 py-3 text-base font-semibold text-sand transition-colors hover:border-sand hover:bg-sand hover:text-ink"
              >
                See the campaigns
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={2} />
              </a>
            </FadeUp>
          </div>

          <div className="lg:col-span-6 lg:col-start-7">
            <dl className="divide-y divide-sand/15">
              {figures.map((f, i) => (
                <FadeUp key={f.key} delay={0.08 * i}>
                  <div className="flex items-baseline justify-between gap-6 py-7 first:pt-0 last:pb-0" data-testid={`${testid}-figure-${f.key}`}>
                    <div>
                      <dt className="text-[13px] font-semibold uppercase tracking-[0.2em] text-sand/60">{f.label}</dt>
                      <dd className="mt-1 text-[13px] text-sand/60">{f.note}</dd>
                    </div>
                    <dd className="font-serif text-[3.5rem] lg:text-[5.5rem] leading-none tracking-tight text-sand tabular-nums">
                      <CountUp to={f.value} prefix={f.prefix || ""} duration={1.4} data-testid={`${testid}-value-${f.key}`} />
                    </dd>
                  </div>
                </FadeUp>
              ))}
            </dl>
            <FadeUp delay={0.3}>
              <p className="mt-8 text-[13px] leading-relaxed text-sand/45" data-testid={`${testid}-footnote`}>
                Figures reflect grants My Rich Aunt has sent through Stripe{since ? ` since ${since}` : ""}. Donations are pooled; grants are made at the organization's discretion under its 501(c)(3) program.
                {!data.livemode && <span className="ml-2 rounded-full bg-yellow/20 px-2 py-0.5 text-[13px] font-semibold uppercase tracking-[0.16em] text-yellow">Sandbox figures</span>}
              </p>
            </FadeUp>
          </div>
        </div>
      </div>
    </section>
  );
};
